import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import {
  FaGithub,
  FaLinkedin,
  FaTwitter,
  FaInstagram,
  FaYoutube,
  FaEnvelope,
  FaClock,
} from 'react-icons/fa';

function Icon(component: any, className?: string) {
  return React.createElement(component as React.ComponentType<{ className?: string }>, { className });
}

const socialLinks = [
  { icon: FaGithub, url: 'https://github.com/evodenuby', label: 'GitHub', color: 'hover:text-textPrimary' },
  { icon: FaLinkedin, url: 'https://www.linkedin.com/in/evode-muyisingizemwese', label: 'LinkedIn', color: 'hover:text-[#0A66C2]' },
  { icon: FaTwitter, url: 'https://twitter.com/evodeSTACK', label: 'Twitter', color: 'hover:text-[#1DA1F2]' },
  { icon: FaInstagram, url: 'https://instagram.com/evodenuby', label: 'Instagram', color: 'hover:text-[#E1306C]' },
  { icon: FaYoutube, url: 'https://youtube.com/@evodenuby', label: 'YouTube', color: 'hover:text-[#FF0000]' },
  { icon: FaEnvelope, url: 'mailto:evodemuyisingize@gmail.com', label: 'Email', color: 'hover:text-[#EA4335]' },
];

const navLinks = [
  { name: 'Home', href: '#home' },
  { name: 'About', href: '#about' },
  { name: 'Skills', href: '#skills' },
  { name: 'Projects', href: '#projects' },
  { name: 'Contact', href: '#contact' },
];

const Footer: React.FC = () => {
  const year = new Date().getFullYear();
  const [kigaliTime, setKigaliTime] = useState({ day: '', time: '' });

  useEffect(() => {
    const update = () => {
      const now = new Date();
      setKigaliTime({
        day: now.toLocaleDateString('en-US', { weekday: 'long', timeZone: 'Africa/Kigali' }),
        time: now.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: true, timeZone: 'Africa/Kigali' }),
      });
    };
    update();
    const id = setInterval(update, 1000);
    return () => clearInterval(id);
  }, []);

  return (
    <footer className="relative bg-gradient-to-b from-tertiary/50 to-primary border-t border-border/30">
      <div className="container mx-auto px-4 sm:px-6 py-12">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-10 mb-10">

          {/* Brand */}
          <div className="space-y-3">
            <div className="flex items-center gap-4">
              <div className="relative flex-shrink-0">
                <div className="w-16 h-16 rounded-full border-2 border-border/40 overflow-hidden">
                  <img
                    src="/images/profile2.jpg"
                    alt="EVODENUBY"
                    className="w-full h-full object-cover"
                  />
                </div>
                <div className="absolute -bottom-0.5 -right-0.5 w-3 h-3 bg-green-400 rounded-full border-2 border-primary" />
              </div>
              <div>
                <h3 className="text-lg font-bold text-textPrimary">
                  EVODENUBY<span className="text-secondary">.</span>
                </h3>
                <p className="text-xs text-textSecondary">Full-Stack Developer</p>
              </div>
            </div>
            <p className="text-sm text-textSecondary leading-relaxed max-w-xs">
              MERN Full-Stack Developer. Crafting
              scalable, clean digital solutions.
            </p>
            <div className="flex flex-wrap gap-2 pt-1">
              {['React', 'Node.js', 'TypeScript', 'Python', 'MongoDB'].map((t) => (
                <span
                  key={t}
                  className="px-2 py-0.5 text-[11px] font-mono rounded border border-border/40 text-textSecondary/70"
                >
                  {t}
                </span>
              ))}
            </div>
          </div>

          {/* Quick Links */}
          <div className="space-y-3">
            <h4 className="text-sm font-bold text-textPrimary uppercase tracking-wider">Navigation</h4>
            <div className="space-y-2">
              {navLinks.map((link) => (
                <a
                  key={link.name}
                  href={link.href}
                  className="block text-sm text-textSecondary hover:text-secondary transition-colors duration-300"
                >
                  {link.name}
                </a>
              ))}
            </div>
          </div>

          {/* Contact */}
          <div className="space-y-3">
            <h4 className="text-sm font-bold text-textPrimary uppercase tracking-wider">Contact</h4>
            <div className="space-y-2 text-sm text-textSecondary">
              <a href="mailto:evodemuyisingize@gmail.com" className="block hover:text-secondary transition-colors duration-300">
                evodemuyisingize@gmail.com
              </a>
              <a href="tel:+250791783308" className="block hover:text-secondary transition-colors duration-300">
                +250 791 783 308
              </a>
              <p>Kigali, Rwanda</p>
            </div>

            {/* Live Kigali Time */}
            <div className="flex items-center gap-2 mt-3 px-3 py-2 rounded-lg bg-tertiary/50 border border-border/30">
              {Icon(FaClock, 'text-xs text-secondary')}
              <div>
                <p className="text-[11px] font-mono text-textSecondary/60">{kigaliTime.day}</p>
                <p className="text-sm font-mono font-bold text-secondary">{kigaliTime.time}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Divider */}
        <div className="border-t border-border/30 pt-6 flex flex-col sm:flex-row items-center justify-between gap-4">
          {/* Copyright */}
          <p className="text-xs text-textSecondary/60">
            &copy; {year}{' '}
            <a href="#home" className="font-semibold text-textSecondary hover:text-secondary transition-colors">
              EVODENUBY
            </a>
            . All rights reserved.
          </p>

          {/* Social Icons */}
          <div className="flex items-center gap-4">
            {socialLinks.map((s) => (
              <motion.a
                key={s.label}
                href={s.url}
                target="_blank"
                rel="noopener noreferrer"
                whileHover={{ y: -2 }}
                className={`text-textSecondary/50 transition-colors duration-300 ${s.color}`}
                aria-label={s.label}
              >
                {Icon(s.icon, 'text-base')}
              </motion.a>
            ))}
          </div>
        </div>
      </div>

    </footer>
  );
};

export default Footer;
